import { objectEntries } from "@toss/utils";
import moment from "moment";
import {
  getOpenAIExecutionAsync,
  getOpenAIMessagesAsync,
  postOpenAIExecutionAsync,
  postOpenAIFileUploadAsync,
  postOpenAIMessageAsync,
  postOpenAIThreadAsync,
} from "src/api/question/qeustion-api";

import { axiosInstanceNoLoading, YmdFormat } from "src/utils";
import prompt, { PromptKeys } from "./prompt";

const analysisPrompts = objectEntries(prompt);

export interface AIAnalysisData {
  promptKey: PromptKeys;
  content: string;
}
export interface AIAnalysisResult {
  success: boolean;
  data?: AIAnalysisData[];
  error?: Error;
}

/**
 * CS AI 분석 hook
 * AI 분석 API 사용방법 링크
 * https://pnpt.atlassian.net/wiki/spaces/MVPD/pages/879329605/2024.12.23+-+CS+AI+OpenAI+Platform+api#02-Thread-%EC%83%9D%EC%84%B1
 * @returns
 */
export const useAIAnalysis = () => {
  const startAnalysis = async (dataRange: [Date, Date], xlsx: File, signal?: AbortSignal): Promise<AIAnalysisResult> => {
    const startTime = Date.now(); // 시작 시간 기록
    console.log("🔍 Analysis started");

    try {
      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }

      // 1. 파일 업로드
      const uploadResponse = await postOpenAIFileUploadAsync(axiosInstanceNoLoading, { file: xlsx });

      if (uploadResponse.status !== 200 || !uploadResponse.data.data?.id) {
        throw new Error(`파일 업로드 실패: ${uploadResponse.data || "알 수 없는 오류"}`);
      }

      const fileId = uploadResponse.data.data.id;

      // 내부 AbortController - 하나의 프로세스가 실패하면 다른 모든 프로세스를 취소
      const internalAbortController = new AbortController();
      const internalSignal = internalAbortController.signal;

      // 외부 signal이 abort되면 내부도 abort
      signal?.addEventListener("abort", () => internalAbortController.abort());

      // 병렬 처리를 위한 함수
      const processPrompt = async (promptKey: PromptKeys, prompt: string, index: number): Promise<AIAnalysisData> => {
        let retryCount = 0;
        if (internalSignal.aborted) throw new Error("Analysis cancelled");

        console.log(`🚀 시작: ${promptKey}`);

        // 인덱스에 따른 딜레이 추가
        await new Promise((resolve) => setTimeout(resolve, index * 100));

        // 각 프롬프트마다 새로운 스레드 생성
        const newThreadResponse = await postOpenAIThreadAsync(axiosInstanceNoLoading, {
          fileId,
          message: `Ctrlroom CS from ${moment(dataRange[0]).format(YmdFormat.YYYY_MM_DD)} to ${moment(dataRange[1]).format(YmdFormat.YYYY_MM_DD)}`,
        });
        const newThreadId = newThreadResponse.data.data.id;

        // 3. Message 생성
        await postOpenAIMessageAsync(axiosInstanceNoLoading, {
          threadId: newThreadId, // 새로운 스레드 ID 사용
          content: prompt,
          attachments: [
            {
              fileId: fileId,
              tools: [{ type: "code_interpreter" }],
            },
          ],
        });

        // 4. Run 생성
        const runResponse = await postOpenAIExecutionAsync(axiosInstanceNoLoading, {
          threadId: newThreadId, // 새로운 스레드 ID 사용
          assistantId: process.env.REACT_APP_ASSISTANT! || "asst_6Uy89yh1yE4ix8bGJWuANCoH",
          tools: [{ type: "code_interpreter" }],
        });
        const runId = runResponse.data.data.id;

        // 5. Run 상태 확인 (개선된 폴링 로직)
        const checkStatus = async (): Promise<void> => {
          if (internalSignal.aborted) throw new Error("Analysis cancelled");
          retryCount++;
          const statusResponse = await getOpenAIExecutionAsync(axiosInstanceNoLoading, {
            threadId: newThreadId,
            runId,
          });

          const status = statusResponse.data.data.status;
          console.log(`${promptKey}: ${retryCount}번째 시도 - ${status}`);

          if (status === "failed") {
            throw new Error(`Run status check failed prompt: ${promptKey}
              statusResponse:${JSON.stringify(statusResponse.data)}
              `);
          }
          if (status !== "completed") {
            if (internalSignal.aborted) throw new Error("Analysis cancelled");
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return checkStatus();
          }

          console.log(`✅ 완료: ${promptKey} (총 ${retryCount}회 시도)`);
        };

        await checkStatus();

        // 6. 결과 메시지 조회
        const messagesResponse = await getOpenAIMessagesAsync(axiosInstanceNoLoading, {
          threadId: newThreadId,
          query: { limit: 1, runId },
        });

        const messageArray: { text: { value: string } | null }[] = messagesResponse?.data?.data?.content?.[0]?.content;
        if (messageArray && messageArray.length > 1) {
          console.warn("multiple answers found for prompt:", promptKey, messageArray.length, newThreadId);
        }
        const content = messageArray?.reduce<string>((msg, item) => {
          if (!item?.text?.value) return msg;
          return msg + item.text.value;
        }, "");

        return {
          promptKey,
          content,
        };
      };

      // 병렬 처리로 변경하고 인덱스 전달
      // 하나라도 실패하면 나머지 모두 취소
      const analysisResults = await Promise.all(
        analysisPrompts.map(([promptKey, prompt], index) =>
          processPrompt(promptKey, prompt, index).catch((error) => {
            internalAbortController.abort();
            throw error;
          }),
        ),
      );

      console.log("\n🎉 All prompts processed successfully! analysisResults:", analysisResults);
      return {
        success: true,
        data: analysisResults,
      };
    } catch (error) {
      console.error("❌ Error during analysis:", error);
      return {
        success: false,
        error: error as Error,
      };
    } finally {
      const endTime = Date.now(); // 종료 시간 기록
      const duration = (endTime - startTime) / 1000; // 초 단위로 변환

      console.log(`⏱️ Analysis completed in ${duration} seconds`);
    }
  };

  return {
    startAnalysis,
  };
};
